import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';

import { ErrorInfo } from '#shared/types/common';
import { ROUTES } from 'constants/routes';
import { rateLimitCriticalErrorProps } from 'constants/errorProps';
import LoaderIcon from 'components/LoaderImage';
import LogoIcon from 'components/LogoImage';
import ErrorLayoutCommon from '#web-components/components/Layouts/Error';
import { getRegistryTitle, getRegistryTitleFull } from '#shared/utils/registrySettings';
import ErrorNavLink from './components/ErrorNavLink';

type ErrorLayoutProps = {
  error: ErrorInfo
};

export default function ErrorLayout({ error: errorProps }: ErrorLayoutProps) {
  const { t, i18n } = useTranslation('components');
  const dispatch = useDispatch();
  const registryTitle = getRegistryTitle(REGISTRY_SETTINGS);
  const registryTitleFull = getRegistryTitleFull(REGISTRY_SETTINGS);

  const handleBackLinkClick = useCallback((path) => {
    dispatch(push(path));
  }, [dispatch]);

  let error = errorProps;

  const systemError = useMemo(() => {
    if (!error.isSystemError) {
      return undefined;
    }

    const errorInfoFields = [{
      name: t('errorLayout.pageUrl'),
      value: window.location.href,
    }];

    if (error.traceId) {
      errorInfoFields.unshift({
        name: t('errorLayout.traceId'),
        value: error.traceId,
      });
    }
    return {
      title: t('errorLayout.systemErrorDescription'),
      fields: errorInfoFields,
    };
  }, [error.isSystemError, error.traceId, t]);

  if (error.httpStatus === 429) {
    error = rateLimitCriticalErrorProps;
  }

  return (
    <ErrorLayoutCommon
      t={i18n.getFixedT(i18n.language, 'domains')}
      registryName={registryTitle}
      registryFullName={registryTitleFull}
      error={error}
      reloadButtonCaption={t('errorLayout.reloadPage')}
      defaultErrorTitle={t('errorLayout.errorTitle')}
      defaultBackLinkTitle={t('errorLayout.backToHome')}
      defaultMessage={t('errorLayout.smthWentWrong')}
      defaultDescription={t('errorLayout.reloadPageAndContactAdministrator')}
      homePath={ROUTES.PROCESS_LIST}
      navLinkComponent={ErrorNavLink}
      appTitle={t('text:appTitle')}
      onBackLinkClick={handleBackLinkClick}
      systemError={systemError}
      loaderIcon={<LoaderIcon />}
      logoIcon={<LogoIcon />}
      supportEmail={{
        title: t('errorLayout.needHelp'),
        description: t('errorLayout.copySystemErrorDescription'),
        email: REGISTRY_ENVIRONMENT_VARIABLES.supportEmail,
        subject: t('errors:supportEmail.subject'),
        body: t('errors:supportEmail.body'),
      }}
      supportChannelUrl={REGISTRY_ENVIRONMENT_VARIABLES.supportChannelUrl}
    />
  );
}
